import React, { memo } from "react";
import styled from "styled-components";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { LOAD_INTERVAL, RETURN_VIEW, retrieveBatch } from "./utils";
import { parseRestServer } from "views/Config/REST/api";
import { parseBillingStatusDetail } from "views/Status/Billing/api.js";
import { parseParameters as parseParametersRadius } from "views/Config/RADIUS/api";
import useDashboardData from "./useDashboardData";
import Loading from "common/Loading";
import { bqnIdTarget } from "common/api";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-primary);

  svg {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 12px;
  line-height: 20px;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

// What separates the functionalities of the packet engine from those of the
// APIs, which go under no heading of their own.
const Gap = styled.div`
  margin-top: 10px;
`;

// The functionalities whose labels are short enough to go two in a row, so
// that the card holds them in half of the lines.
const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: var(--text-primary);
`;

// The billing status is indexed by BQN, and the response of the command of a
// single BQN reports no BQN ID, so the BQN selected is the one its status is
// attributed to, and the BQN 0 the one of a response of no BQN in particular.
//
// The REST server and the RADIUS parameters answer for the whole cluster:
// their commands take no "bqn-id ID" option.
//
// The three go in a single batch request: the CLI runs the commands of the
// GUI one after the other, so one request each would take one turn of its
// queue each, and this is asked for again on every LOAD_INTERVAL.
const apiRetrievals = (bqnIdShown) => [
  {
    name: "rest",
    command: "show api rest server",
    parse: parseRestServer,
    atCluster: true,
  },
  {
    name: "radius",
    command: "show api radius parameters",
    parse: parseParametersRadius,
    atCluster: true,
  },
  {
    name: "billing",
    command: "show api billing status detail",
    parse: (response) => parseBillingStatusDetail(response, bqnIdShown),
  },
];

const getActiveFunctionalities = ({ mode, bqnId }) => {
  const bqnIdShown = bqnIdTarget(mode, bqnId) ?? "0";

  return retrieveBatch(apiRetrievals(bqnIdShown), mode, bqnId).then(
    ({ rest, radius, billing }) => {
      // A BQN with the billing API off reports no status at all, and the one
      // that reports it names the billing it syncs the subscribers with
      // among the facts of it.
      const facts = billing?.[bqnIdShown] ?? [];

      return {
        radius: radius.enabled,
        rest: rest.enabled,
        billing: facts.length > 0,
        billingType: facts.find(({ label }) => label === "Type")?.value,
      };
    },
  );
};

// What the parameters of the packet engine tell of the functionalities. The
// dashboard asks for them with the status of the BQN, so they arrive here
// already retrieved.
const functionalitiesOfParameters = (parameters) => {
  const { acm, tcp, dscpMarking, shaping, bypassIPv4, bypassIPv6 } =
    parameters ?? {};

  return {
    acm: acm?.enabled ?? false,
    tcp,
    dscp: dscpMarking,
    shapingPerFlow: shaping?.perFlow ?? false,
    shapingPerSubscriber: shaping?.perSubscriber ?? false,
    shapingRatePerSubscriber: shaping?.ratePerSubscriber ?? false,
    shapingRatePerSubscriberGroup: shaping?.ratePerSubscriberGroup ?? false,
    // A BQN that does not report the bypass state has none active.
    bypassIPv4: bypassIPv4 === "yes",
    bypassIPv6: bypassIPv6 === "yes",
  };
};

const OPTIMIZATION_SHOWN = [
  { name: "tcp", label: "TCPO", view: "viewOptimizationConf" },
  { name: "bypassIPv4", label: "Bypass IPv4", view: "viewOptimizationConf" },
  { name: "acm", label: "ACM", view: "viewOptimizationConf" },
  { name: "bypassIPv6", label: "Bypass IPv6", view: "viewOptimizationConf" },
  { name: "dscp", label: "DSCP", view: "viewOptimizationConf" },
];

const FUNCTIONALITIES_SHOWN = [
  { label: "Shaping" },
  {
    name: "shapingPerFlow",
    label: "Individual flow",
    view: "viewOptimizationConf",
  },
  {
    name: "shapingPerSubscriber",
    label: "Aggregated flow",
    view: "viewOptimizationConf",
  },
  {
    name: "shapingRatePerSubscriber",
    label: "Subscriber rate limiting",
    view: "viewOptimizationConf",
  },
  {
    name: "shapingRatePerSubscriberGroup",
    label: "Subscriber-group rate limiting",
    view: "viewOptimizationConf",
  },
  {},
  { name: "radius", label: "RADIUS", view: "viewRADIUSConf" },
  { name: "rest", label: "REST", view: "viewRESTConf" },
  {
    name: "billing",
    label: ({ billingType }) =>
      billingType === undefined ? "Billing" : `Billing (${billingType})`,
    view: "viewBillingConf",
  },
];

function ActiveFunctionalities({ parameters }) {
  // The card has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data: apis, isLoading } = useDashboardData({
    retrieve: getActiveFunctionalities,
    onError: "Error fetching active functionalities data:",
    interval: LOAD_INTERVAL,
  });

  // The parameters come from the status of the BQN, which the dashboard
  // retrieves, so what is drawn waits for them as well.
  if (isLoading || parameters === undefined) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (apis === null) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  const functionalities = {
    ...apis,
    ...functionalitiesOfParameters(parameters),
  };

  const itemOf = ({ name, label, view }) => (
    <Item
      key={name}
      onClick={() => globalNavigate(view, { returnView: RETURN_VIEW })}
    >
      {functionalities[name] ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      <Label>
        {typeof label === "function" ? label(functionalities) : label}
      </Label>
    </Item>
  );

  return (
    <Wrapper>
      <Columns>{OPTIMIZATION_SHOWN.map(itemOf)}</Columns>
      {FUNCTIONALITIES_SHOWN.map((functionality, index) => {
        const { name, label } = functionality;

        if (name === undefined) {
          return label === undefined ? (
            <Gap key={index} />
          ) : (
            <Subtitle key={label}>{label}</Subtitle>
          );
        }

        return itemOf(functionality);
      })}
    </Wrapper>
  );
}

export default memo(ActiveFunctionalities);
